import React, { useState, useEffect, Suspense } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Avatar } from "@mui/material";
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import { TiWeatherPartlySunny } from "react-icons/ti";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ProductIcon from "@mui/icons-material/Category";
import SalesStatementIcon from "@mui/icons-material/BarChart";
import InsightsIcon from "@mui/icons-material/Insights";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ListIcon from '@mui/icons-material/List';
import PostAddIcon from '@mui/icons-material/PostAdd';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import { GoGraph } from "react-icons/go";
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import TaskIcon from "@mui/icons-material/Task";
import { GiFertilizerBag } from "react-icons/gi";


// Menu configuration
const menuItems = {
    "Point of Sale": [
        { name: "Sale", icon: <ShoppingCartIcon />, route: "pos/sale" },
        { name: "Inventory", icon: <Inventory2OutlinedIcon />, route: "pos/inventory" },
        { name: "Report", icon: <AssessmentIcon />, route: "pos/report" },
        { name: "Product", icon: <ProductIcon />, route: "pos/report" },
        { name: "Sales Statement", icon: <SalesStatementIcon />, route: "pos/report" },
    ],
    "Market Insights": [
        { name: "Daily Market News", icon: <NewspaperIcon />, route: "market/daily-news" },
        { name: "Mandi Price", icon: <GoGraph />, route: "market/mandi" },
        { name: "Market Insights", icon: <InsightsIcon />, route: "market/insights" },
    ],
    "Farmer Management": [
        { name: "Dashboard", icon: <DashboardOutlinedIcon />, route: "farmer/dashboard" },
        { name: "Purchase", icon: <LocalOfferOutlinedIcon /> },
        { name: "Cultivation", icon: <LocalFloristIcon />, route: "farmer/cultivation" },
        { name: "Credit", icon: <CreditCardOutlinedIcon /> },
        { name: "Support", icon: <HeadsetMicOutlinedIcon /> },
        { name: "Farmer Registration", icon: <PersonAddOutlinedIcon />, route: "farmer/registration" },
    ],
    "Crop Insights": [
        { name: "Crop Library", icon: <LocalFloristIcon />, route: "crop/library" },
        { name: "Crop Calendar", icon: <CalendarTodayOutlinedIcon /> },
        { name: "Crop Health", icon: <InsightsIcon /> },
        { name: "FertiCal", icon: <GiFertilizerBag /> },
    ],
    "Enquiry Management": [
        { name: "View Enquiry", icon: <VisibilityIcon />, route: "enquiry/view" },
        { name: "My Enquiries", icon: <ListIcon />, route: "enquiry/my" },
        { name: "Post Enquiry", icon: <PostAddIcon />, route: "enquiry/post" },
    ],
    "Fpo Management": [
        { name: "Manage Accounting", icon: <TaskIcon />, route: "FpoManagement/FpoManage" },
    ]
};

const TopNav: React.FC = () => {
    const [activeMenu, setActiveMenu] = useState<keyof typeof menuItems>("Point of Sale");
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [userName, setUserName] = useState<string | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    // Sync submenu highlight with URL
    useEffect(() => {
        const path = location.pathname.replace("/top/", "");
        const found = Object.values(menuItems)
            .flat()
            .find((item: any) => item.route === path);
        if (found) setActiveSubmenu(found.name);
        setUserEmail(localStorage.getItem("dealerMail"));
        setUserName(localStorage.getItem("dealerName"));
    }, [location.pathname]);

    const handleMenuClick = (menu: keyof typeof menuItems) => {
        setActiveMenu(menu);
        setActiveSubmenu(null);
        // navigate to first submenu if desired
        const firstItem = menuItems[menu][0];
        if ('route' in firstItem && firstItem.route) {
            navigate(`/top/${firstItem.route}`);
        }
    };

    const handleSubmenuClick = (item: { name: string; route: string }) => {
        setActiveSubmenu(item.name);
        navigate(`/top/${item.route}`);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    return (
        <div className="flex flex-col h-screen">
            {/* Top Bar */}
            <div className="flex items-center justify-between bg-white shadow-lg h-20 px-6 fixed top-0 w-full z-50">
                <img src="Images/logoname.png" alt="Logo" className="h-12 sm:h-16" />
                <ul className="flex space-x-4">
                    {Object.keys(menuItems).map(menu => (
                        <li
                            key={menu}
                            className={`cursor-pointer px-4 py-2 ${activeMenu === menu ? 'bg-green-500 text-white' : 'hover:bg-gray-200'}`}
                            onClick={() => handleMenuClick(menu as any)}
                        >
                            {menu}
                        </li>
                    ))}
                </ul>
                <div className="flex items-center space-x-4">
                    <TiWeatherPartlySunny size={24} />
                    <NotificationsIcon fontSize="large" />
                    <Avatar />
                    <div>
                        <div className="font-semibold">{userName}</div>
                        <div className="text-sm text-gray-500">{userEmail}</div>
                    </div>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-1 mt-20">
                {/* Sidebar */}
                <div className="w-64 bg-white shadow-lg border-r fixed h-full">
                    <ul className="space-y-2 mt-2">
                        {menuItems[activeMenu].map((item: any) => (
                            <li
                                key={item.name}
                                className={`ml-2 mr-5 transition flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer ${activeSubmenu === item.name
                                    ? "bg-[#f0fdf4] text-[#3fc041] border-l-4 border-[#3fc041]"
                                    : "hover:bg-gray-100 text-gray-800"
                                    }`}
                                onClick={() => handleSubmenuClick(item)}
                            >
                                <span className={`text-xl ${activeSubmenu === item.name ? "text-[#3fc041]" : "text-gray-500"
                                    }`}>{item.icon}</span>
                                <span className={`${activeSubmenu === item.name ? "font-semibold" : ""}`}>{item.name}</span>
                            </li>
                        ))}
                    </ul>
                    <button onClick={handleLogout} className="p-2 hover:cursor-pointer bg-[#3fc041] fixed bottom-10 ">
                        <LogoutIcon className="mr-2" /> Logout
                    </button>
                </div>

                {/* Main Content via Outlet */}
                <div className="flex-1 p-0 bg-[#f6f6f6] ml-64 z-0">
                    <Suspense fallback={<div>Loading...</div>}>
                        <Outlet />
                    </Suspense>
                </div>
            </div>
        </div>
    );
};

export default TopNav;
